import { StandardButton } from '@/components/buttons';
import { WorkflowFooter } from '@/components/workflow/workflow-footer';
import { Avatar, Button, Flex, Text } from '@mantine/core';
import {
	IconBarcode,
	IconCameraPlus,
	IconCheck,
	IconChecklist,
	IconInfoCircle,
	IconPlus,
	IconTruckDelivery,
} from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useMemo } from 'react';
import { useBaleInboundContext } from './hook';
import { ManualInboundScannedMaterialsDisplay } from './subroutes/inbound-bale-scan-add';

const defaultWorkflowSteps = [
	{
		label: 'Receive',
		icon: <IconTruckDelivery strokeWidth={1.5} style={{ color: 'white' }} size={22} />,
		value: 'RECEIVING',
	},
	{
		label: 'Evaluate',
		icon: <IconCameraPlus strokeWidth={1.5} style={{ color: 'white' }} size={22} />,
		value: 'EVALUATION',
	},
	{
		label: 'Confirm',
		icon: <IconChecklist strokeWidth={1.5} style={{ color: 'white' }} size={22} />,
		value: 'CONFIRMATION',
	},
];

export const BaleInboundFooter = () => {
	const {
		page,
		setManualCodeEntryOpen,
		manualCodeEntryOpen,
		workflowType,
		scannedBales,
		setPage,
		canSubmitManualCreate,
		isConfirmed,
		handleCreateInboundLoad,
		isSubmitting,
	} = useBaleInboundContext();
	const navigate = useNavigate();

	const scannedMaterials = scannedBales.filter((bale) => bale.count > 0);

	/** Whether any bales have been scanned */
	const hasScans = scannedMaterials.length > 0;

	const workflowSteps = useMemo(() => {
		if (workflowType === 'MANUAL') {
			return [
				{
					label: 'Create',
					icon: <IconPlus strokeWidth={1.5} style={{ color: 'white' }} size={22} />,
					value: 'CREATE',
				},
				...defaultWorkflowSteps.slice(1),
			];
		}
		return defaultWorkflowSteps;
	}, [workflowType]);

	const getStepStatus = useCallback(
		(step: (typeof defaultWorkflowSteps)[number]) => {
			if (step.value === 'CREATE') {
				if (page === 'INITIAL_SCAN') {
					return 'INACTIVE';
				}
				if (page === 'MANUAL_CREATE:ROOT') {
					return 'ACTIVE';
				}
				return 'COMPLETE';
			} else if (step.value === 'RECEIVING') {
				if (page === 'INITIAL_SCAN') {
					return 'INACTIVE';
				}
				if (page === 'RECEIVING' || page === 'DOCKET_EDIT') {
					return 'ACTIVE';
				}
				return 'COMPLETE';
			} else if (step.value === 'EVALUATION') {
				if (['INITIAL_SCAN', 'RECEIVING', 'MANUAL_CREATE:ROOT', 'DOCKET_PHOTO'].includes(page)) {
					return 'INACTIVE';
				}
				if (page === 'EVALUATION') {
					return 'ACTIVE';
				}
				return 'COMPLETE';
			} else if (step.value === 'CONFIRMATION') {
				if (
					[
						'INITIAL_SCAN',
						'RECEIVING',
						'DOCKET_EDIT',
						'MANUAL_CREATE:ROOT',
						'EVALUATION',
						'DOCKET_PHOTO',
					].includes(page)
				) {
					return 'INACTIVE';
				}
				if (page === 'CONFIRMATION') {
					return 'ACTIVE';
				}
				return 'COMPLETE';
			}
		},
		[page]
	);

	const footerContent = useMemo(() => {
		switch (page) {
			case 'INITIAL_SCAN':
				return {
					actions: (
						<>
							<Button
								size="lg"
								variant="filled"
								fullWidth
								style={{ fontSize: 18 }}
								onClick={() => setManualCodeEntryOpen(true)}
								leftSection={<IconBarcode size={16} />}
							>
								Manual Code Entry
							</Button>
						</>
					),
					secondary: (
						<Flex pb="xl" pt="md" px="lg" align="center" gap="xs">
							<IconInfoCircle size={16} />
							<Text size="sm">Scan a single bale from the load to get started</Text>
						</Flex>
					),
				};
			case 'DOCKET_EDIT':
				return {
					actions: (
						<>
							<StandardButton onClick={() => setPage('RECEIVING')}>Save Changes</StandardButton>
						</>
					),
				};
			case 'SCAN_ADD':
				return {
					actions: (
						<>
							<Button
								size="lg"
								variant="filled"
								fullWidth
								style={{ fontSize: 18 }}
								onClick={() => setManualCodeEntryOpen(true)}
								leftSection={<IconBarcode size={16} />}
							>
								Manual Code Entry
							</Button>
							{hasScans && (
								<Button
									size="lg"
									variant="filled"
									fullWidth
									style={{ fontSize: 18 }}
									onClick={() => setPage('RECEIVING')}
								>
									Accept
								</Button>
							)}
						</>
					),
					secondary: hasScans ? (
						<ManualInboundScannedMaterialsDisplay materials={scannedMaterials} />
					) : null,
				};
			case 'MANUAL_CREATE:ADD_BALE':
				return {
					actions: (
						<>
							<Button
								size="lg"
								variant="filled"
								fullWidth
								style={{ fontSize: 18 }}
								onClick={() => setManualCodeEntryOpen(true)}
								leftSection={<IconBarcode size={16} />}
							>
								Manual Code Entry
							</Button>
						</>
					),
					secondary: hasScans ? (
						<ManualInboundScannedMaterialsDisplay materials={scannedMaterials} />
					) : null,
				};
			case 'MANUAL_CREATE:REMOVE_BALE':
				return {
					actions: (
						<>
							<Button
								size="lg"
								variant="filled"
								color="red"
								fullWidth
								style={{ fontSize: 18 }}
								onClick={() => setManualCodeEntryOpen(true)}
								leftSection={<IconBarcode size={16} />}
							>
								Manual Code Entry
							</Button>
						</>
					),
					secondary: hasScans ? (
						<ManualInboundScannedMaterialsDisplay materials={scannedMaterials} />
					) : null,
				};
			case 'MANUAL_CREATE:ROOT':
				return {
					actions: (
						<>
							<StandardButton
								disabled={!canSubmitManualCreate}
								onClick={() => setPage('EVALUATION')}
							>
								Continue
							</StandardButton>
						</>
					),
					secondary: (
						<Flex align="center" justify="space-between" pb="lg" pos="relative" px="xl">
							{workflowSteps.map((step) => {
								const status = getStepStatus(step);
								const icon =
									status === 'COMPLETE' ? (
										<IconCheck strokeWidth={1.5} style={{ color: 'white' }} size={22} />
									) : (
										step.icon
									);
								const color =
									status === 'COMPLETE' ? 'green' : status === 'ACTIVE' ? 'green' : 'grey';
								return (
									<Flex key={step.value} direction="column" pt="sm" align="center">
										<Avatar size="md" radius="xl" bg={color}>
											{icon}
										</Avatar>
										<p style={{ fontSize: 14, color: 'grey', margin: 0 }}>{step.label}</p>
									</Flex>
								);
							})}
						</Flex>
					),
				};
			case 'EVALUATION':
				return {
					actions: (
						<>
							<StandardButton onClick={() => setPage('CONFIRMATION')}>Continue</StandardButton>
						</>
					),
					secondary: (
						<Flex align="center" justify="space-between" pb="lg" pos="relative" px="xl">
							{workflowSteps.map((step) => {
								const status = getStepStatus(step);
								const icon =
									status === 'COMPLETE' ? (
										<IconCheck strokeWidth={1.5} style={{ color: 'white' }} size={22} />
									) : (
										step.icon
									);
								const color =
									status === 'COMPLETE' ? 'green' : status === 'ACTIVE' ? 'green' : 'grey';
								return (
									<Flex key={step.value} direction="column" pt="sm" align="center">
										<Avatar size="md" radius="xl" bg={color}>
											{icon}
										</Avatar>
										<p style={{ fontSize: 14, color: 'grey', margin: 0 }}>{step.label}</p>
									</Flex>
								);
							})}
						</Flex>
					),
				};
			case 'RECEIVING':
				return {
					actions: (
						<>
							<StandardButton onClick={() => setPage('EVALUATION')}>Continue</StandardButton>
						</>
					),
					secondary: (
						<Flex align="center" justify="space-between" pb="lg" pos="relative" px="xl">
							{workflowSteps.map((step) => {
								const status = getStepStatus(step);
								const icon =
									status === 'COMPLETE' ? (
										<IconCheck strokeWidth={1.5} style={{ color: 'white' }} size={22} />
									) : (
										step.icon
									);
								const color =
									status === 'COMPLETE' ? 'green' : status === 'ACTIVE' ? 'green' : 'grey';
								return (
									<Flex key={step.value} direction="column" pt="sm" align="center">
										<Avatar size="md" radius="xl" bg={color}>
											{icon}
										</Avatar>
										<p style={{ fontSize: 14, color: 'grey', margin: 0 }}>{step.label}</p>
									</Flex>
								);
							})}
						</Flex>
					),
				};
			case 'CONFIRMATION':
				return {
					actions: (
						<>
							<StandardButton
								disabled={!isConfirmed || isSubmitting}
								onClick={handleCreateInboundLoad}
							>
								{isSubmitting ? 'Submitting...' : 'Confirm'}
							</StandardButton>
						</>
					),
					secondary: (
						<Flex align="center" justify="space-between" pb="lg" pos="relative" px="xl">
							{workflowSteps.map((step) => {
								const status = getStepStatus(step);
								const icon =
									status === 'COMPLETE' ? (
										<IconCheck strokeWidth={1.5} style={{ color: 'white' }} size={22} />
									) : (
										step.icon
									);
								const color =
									status === 'COMPLETE' ? 'green' : status === 'ACTIVE' ? 'green' : 'grey';
								return (
									<Flex key={step.value} direction="column" pt="sm" align="center">
										<Avatar size="md" radius="xl" bg={color}>
											{icon}
										</Avatar>
										<p style={{ fontSize: 14, color: 'grey', margin: 0 }}>{step.label}</p>
									</Flex>
								);
							})}
						</Flex>
					),
				};
			default:
				return {
					actions: (
						<>
							<StandardButton onClick={() => navigate({ to: '/facility/home' })}>
								Enter Code
							</StandardButton>
						</>
					),
					secondary: (
						<Flex align="center" justify="space-between" pb="lg" pos="relative" px="xl">
							{workflowSteps.map((step) => {
								const status = getStepStatus(step);
								const icon =
									status === 'COMPLETE' ? (
										<IconCheck strokeWidth={1.5} style={{ color: 'white' }} size={22} />
									) : (
										step.icon
									);
								const color =
									status === 'COMPLETE' ? 'green' : status === 'ACTIVE' ? 'green' : 'grey';
								return (
									<Flex key={step.value} direction="column" pt="sm" align="center">
										<Avatar size="md" radius="xl" bg={color}>
											{icon}
										</Avatar>
										<p style={{ fontSize: 14, color: 'grey', margin: 0 }}>{step.label}</p>
									</Flex>
								);
							})}
						</Flex>
					),
				};
		}
	}, [
		page,
		hasScans,
		scannedMaterials,
		canSubmitManualCreate,
		workflowSteps,
		setManualCodeEntryOpen,
		navigate,
		setPage,
		getStepStatus,
		isConfirmed,
		handleCreateInboundLoad,
		isSubmitting,
	]);

	const shouldHideFooter = useMemo(() => {
		if (manualCodeEntryOpen) {
			return true;
		}
		if (page === 'DOCKET_PHOTO') {
			return true;
		}
		if (page === 'ADD_LOAD_PHOTOS') {
			return true;
		}

		if (page === 'MANUAL_CREATE:REMOVE_BALE') {
			return true;
		}
		return false;
	}, [manualCodeEntryOpen, page]);

	return (
		<AnimatePresence initial={false}>
			{!shouldHideFooter && (
				<motion.div
					initial={{ y: 150 }}
					animate={{
						y: 0,
						transition: {
							type: 'spring',
							damping: 25,
							stiffness: 300,
							mass: 0.8,
							delay: 0.1,
						},
					}}
					exit={{
						y: 150,
						transition: {
							type: 'spring',
							damping: 30,
							stiffness: 400,
							mass: 0.6,
							duration: 0.25,
						},
					}}
					style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1000 }}
				>
					<WorkflowFooter secondaryContent={footerContent.secondary}>
						{footerContent.actions}
					</WorkflowFooter>
				</motion.div>
			)}
		</AnimatePresence>
	);
};
